import React from "react";

import { DateInput } from "./GanttControls";
import {
  assigneeLabel,
  barStyle,
  datePercent,
  formatUsDate,
  initials,
  itemStatus,
  shortDate,
  statusLabel
} from "./ganttUtils";

const GanttChart = ({
  dirty,
  gantt,
  humanTeam,
  memberFilter,
  resizing,
  selectedId,
  timelineEnd,
  timelineStart,
  today,
  visibleItems,
  weeks,
  workspaceView,
  workstreamFilter,
  workstreams,
  onMemberFilterChange,
  onResizeStart,
  onSelectItem,
  onUpdateGantt,
  onWorkstreamFilterChange
}) => (
  <>
    <div className="gantt-chart-toolbar">
      <div className="gantt-chart-filters">
        <label className="gantt-workstream-filter">
          Workstream
          <select aria-label="Workstream" value={workstreamFilter} onChange={(event) => onWorkstreamFilterChange(event.target.value)}>
            <option value="all">All workstreams</option>
            {workstreams.map((workstream) => <option key={workstream.key} value={workstream.key}>{workstream.name}</option>)}
          </select>
        </label>
        <div className="gantt-developer-filters" aria-label="Filter Gantt by developer">
          <button className={memberFilter === "all" ? "active" : ""} onClick={() => onMemberFilterChange("all")} title="All developers" aria-label="Show all developers">All</button>
          {humanTeam.map((member) => (
            <button key={member.id} className={memberFilter === String(member.id) ? "active" : ""} onClick={() => onMemberFilterChange(String(member.id))} title={member.name} aria-label={`Filter by ${member.name}`}>
              {member.photo_data_url ? <img src={member.photo_data_url} alt="" /> : <span>{initials(member.name)}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="gantt-chart-dates">
        <label>Project start<DateInput value={gantt.start_date} disabled={workspaceView} onChange={(value) => onUpdateGantt("start_date", value)} /></label>
        <label>Latest workstream deadline<DateInput value={gantt.end_date} disabled onChange={(value) => onUpdateGantt("end_date", value)} /></label>
        <span className={dirty ? "dirty" : ""}>{dirty ? "Unsaved draft" : "Latest version saved"}</span>
      </div>
    </div>
    <div className="gantt-scroll">
      <div className="gantt-grid" style={{ "--weeks": weeks.length }}>
        <div className="gantt-corner">Epic / developer / progress</div>
        <div className="gantt-week-header">
          {weeks.map((week, index) => <div key={week}>W{index + 1}<small>{shortDate(week)}</small></div>)}
          {(gantt.deadlines || []).map((deadline) => (
            <div key={deadline.workstream_key} className="gantt-deadline-line header" title={`${deadline.workstream_name} deadline: ${deadline.due_date}`} style={{ left: `${datePercent(deadline.due_date, timelineStart, timelineEnd)}%`, padding: 0, border: 0 }}>
              <small>{deadline.workstream_name}</small>
            </div>
          ))}
          <div className="gantt-today-line header" title={`Current date: ${today}`} style={{ left: `${datePercent(today, timelineStart, timelineEnd)}%`, padding: 0, border: 0 }} />
        </div>
        {visibleItems.map((item) => {
          const dueDate = workspaceView ? item.due_date : gantt.end_date;
          const status = itemStatus(item, today, dueDate);
          return (
            <React.Fragment key={item.id}>
              <button className={`gantt-label ${selectedId === item.id ? "selected" : ""}`} onClick={() => onSelectItem(item.id)}>
                <strong>{item.title}</strong>
                <span className="gantt-label-developer">Developer: {assigneeLabel(item, humanTeam)}</span>
                <span>{statusLabel(status)} · {item.progress}% · {item.effort_hours}h</span>
              </button>
              <div className="gantt-timeline-row">
                {weeks.map((week) => <div key={week} className="gantt-week-cell" />)}
                <button className={`gantt-bar status-${status} ${resizing?.id === item.id ? "resizing" : ""}`} style={barStyle(item, timelineStart, timelineEnd)} onClick={() => onSelectItem(item.id)}>
                  <span style={{ width: `${item.progress}%` }} /><b>{item.title}</b>
                  <i className="gantt-resize-handle start" aria-label={`Resize ${item.title} start date`} aria-valuemin="0" aria-valuemax="100" aria-valuenow={Math.round(datePercent(item.start_date, timelineStart, timelineEnd))} aria-valuetext={formatUsDate(item.start_date)} role="slider" tabIndex="0" onPointerDown={(event) => onResizeStart(event, item, "start")} />
                  <i className="gantt-resize-handle end" aria-label={`Resize ${item.title} end date`} aria-valuemin="0" aria-valuemax="100" aria-valuenow={Math.round(datePercent(item.end_date, timelineStart, timelineEnd))} aria-valuetext={formatUsDate(item.end_date)} role="slider" tabIndex="0" onPointerDown={(event) => onResizeStart(event, item, "end")} />
                </button>
                <div className="gantt-deadline-line" title={`${item.workstream_name || "Project"} deadline: ${dueDate}`} style={{ left: `${datePercent(dueDate, timelineStart, timelineEnd)}%` }} />
                <div className="gantt-today-line" title={`Current date: ${today}`} style={{ left: `${datePercent(today, timelineStart, timelineEnd)}%` }} />
              </div>
            </React.Fragment>
          );
        })}
        {!visibleItems.length && <div className="gantt-filter-empty">No epics match the selected filters.</div>}
      </div>
    </div>
    <div className="gantt-legend">
      <div className="gantt-status-legend">
        <strong>Status</strong>
        <span className="on-track">On track</span>
        <span className="at-risk">At risk</span>
        <span className="done">Done</span>
        <span className="late">Late</span>
        <span className="deadline">Workstream deadline</span>
        <span className="today">Current date</span>
      </div>
    </div>
  </>
);

export default GanttChart;
